import { useLanguage } from '@/shared/contexts/LanguageContext';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { ThemeToggle } from '@/shared/ui/theme-toggle';
import { Camera, GraduationCap, Heart, Home, Menu, PenTool, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageToggle from './LanguageToggle';

/**
 * /space 的浮动玻璃 pill 导航（桌面底部居中，移动端右下角展开）。
 */
export default function SpaceNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const items = [
    { key: 'spaceHome', href: '/space', icon: Home },
    { key: 'spaceBlog', href: '/space/blog', icon: PenTool },
    { key: 'spacePortfolio', href: '/space/portfolio', icon: Camera },
    { key: 'spaceAbout', href: '/space/about', icon: Heart }
  ];

  const isActive = (href: string) =>
    href === '/space' ? location.pathname === '/space' : location.pathname.startsWith(href);

  return (
    <>
      {/* 桌面端：底部居中玻璃 pill */}
      <nav className="hidden md:block fixed bottom-10 left-1/2 -translate-x-1/2 z-50">
        <div className="glass-effect rounded-2xl px-4 py-2.5 shadow-soft">
          <div className="flex items-center gap-1">
            {items.map(item => (
              <Button
                key={item.key}
                asChild
                variant="ghost"
                size="sm"
                className={`group px-4 py-2 rounded-xl transition-all duration-300 ${
                  isActive(item.href)
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-primary/10 text-foreground/80'
                }`}
              >
                <Link to={item.href}>
                  <item.icon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                  {t(item.key)}
                </Link>
              </Button>
            ))}

            <div className="mx-1 h-6 w-px bg-border/60" />

            <Button
              asChild
              variant="ghost"
              size="sm"
              className="group px-3 py-2 rounded-xl hover:bg-primary/10 text-muted-foreground"
            >
              <Link to="/">
                <GraduationCap className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                {t('backToAcademic')}
              </Link>
            </Button>
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* 移动端：右下角按钮 + 弹层 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden fixed bottom-8 right-6 z-50 p-3 glass-effect rounded-full shadow-soft"
        aria-label="Toggle navigation"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute bottom-24 right-6 w-60">
            <Card className="glass-effect border-0 shadow-soft">
              <div className="p-3 space-y-1">
                {items.map(item => (
                  <Button
                    key={item.key}
                    asChild
                    variant="ghost"
                    className={`w-full justify-start ${
                      isActive(item.href) ? 'bg-primary/10 text-primary' : ''
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <Link to={item.href}>
                      <item.icon className="w-4 h-4 mr-3" />
                      {t(item.key)}
                    </Link>
                  </Button>
                ))}
                <Button
                  asChild
                  variant="ghost"
                  className="w-full justify-start text-muted-foreground"
                  onClick={() => setIsOpen(false)}
                >
                  <Link to="/">
                    <GraduationCap className="w-4 h-4 mr-3" />
                    {t('backToAcademic')}
                  </Link>
                </Button>
                <div className="pt-2 mt-1 border-t border-border flex items-center gap-2">
                  <LanguageToggle />
                  <ThemeToggle />
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
